/**
 * 튜닝 가이드 관리 — **처음부터 프롬프트에 실리는 규칙.**
 *
 * 사례와 다른 물건이다. 사례는 AI 가 **결론을 낸 뒤 찾아서 대조**하지만(그래서 찾히지 않으면
 * 안 쓰인다), 가이드는 **매 턴 프롬프트에 그냥 들어가** 결론이 만들어지는 과정을 제약한다.
 * "인덱스를 늘리기 전에 SQL 재작성을 먼저 검토한다" 같은 집안 규칙이 여기 들어간다.
 *
 * ## 무엇이 실리는지 그대로 보여 준다
 *
 * 규칙이 답을 바꾸는데 무엇이 실렸는지 모르면, 답이 왜 이렇게 나왔는지도 규칙을 어떻게 고쳐야
 * 할지도 알 수 없다. 그래서 **완성된 블록을 글자 수와 함께** 보여 준다.
 *
 * ## 길이 상한이 있는 이유
 *
 * 규칙이 쌓이면 (1) 매 턴 토큰을 먹고 (2) 규칙끼리 모순되기 시작하며 (3) AI 가 어느 것을 따를지
 * **고르게** 된다 — 일관성을 얻으려고 가이드를 쓰는데 정반대의 결과가 된다. 그래서 규칙 하나는
 * 서버가 거부하고(자르지 않는다 — 반쪽 규칙은 뜻이 달라진다), 전체는 예산을 넘으면 뒤에서부터
 * 밀린다. 밀린 것은 이 화면과 대화 중 경고로 **반드시 드러난다.**
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useConfirm } from '../ConfirmDialog';
import { FiPlus, FiSave, FiTrash2, FiEye, FiEyeOff, FiLoader, FiAlertCircle } from 'react-icons/fi';
import { getTuningGuides, saveTuningGuide, deleteTuningGuide } from '../../services/assistantClient';

const EMPTY = { title: '', rule: '', dbms: '', seq: 100 };
const DBMS = ['', 'Oracle', 'PostgreSQL'];

function GuideManager() {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [guides, setGuides] = useState([]);
    const [preview, setPreview] = useState('');
    const [used, setUsed] = useState(0);
    const [max, setMax] = useState(0);
    const [ruleMax, setRuleMax] = useState(300);
    const [droppedIds, setDroppedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState(EMPTY);
    const [editing, setEditing] = useState(null);   // 고치는 중인 가이드 (id)
    const [showPreview, setShowPreview] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getTuningGuides();
            setGuides(r.guides || []);
            setPreview(r.preview || '');
            setUsed(r.used || 0);
            setMax(r.max || 0);
            setRuleMax(r.ruleMax || 300);
            setDroppedIds(r.droppedIds || []);
        } catch (err) {
            toast.error(err.message);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = useCallback(async (body) => {
        setBusy(true);
        try {
            await saveTuningGuide(body);
            setEditing(null);
            if (!body.id) setDraft(EMPTY);
            await load();
        } catch (err) {
            // 길이 초과는 **서버가 거부한다.** 화면에서만 막으면 다른 쓰기 경로로 샌다.
            toast.error(err.message);
        } finally { setBusy(false); }
    }, [load]);

    const remove = useCallback(async (g) => {
        const ok = await confirm({
            title: t('assistant:guideManager.deleteTitle'),
            message: t('assistant:guideManager.confirmDelete', { title: g.title }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        setBusy(true);
        try { await deleteTuningGuide(g.id); await load(); }
        catch (err) { toast.error(err.message); }
        finally { setBusy(false); }
    }, [load, t, confirm]);

    if (loading) return <div className="gm"><FiLoader size={12} className="tcf-spin" /></div>;

    const over = max > 0 && used > max;
    return (
        <div className="gm">
            <div className="gm-intro">{t('assistant:guideManager.intro')}</div>

            {/* **지금 실리는 양**을 늘 보여 준다 — 매 턴 토큰을 먹는 것이라 눈에 보여야 한다 */}
            <div className="gm-budget">
                <span className={over ? 'gm-over' : ''}>
                    {t('assistant:guideManager.used', { used, max })}
                </span>
                <button type="button" className="tcf-btn" onClick={() => setShowPreview(v => !v)}>
                    {showPreview ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                    {t('assistant:guideManager.preview')}
                </button>
            </div>
            {droppedIds.length ? (
                <div className="gm-dropped">
                    <FiAlertCircle size={12} />
                    {t('assistant:guideManager.dropped', { count: droppedIds.length })}
                </div>
            ) : null}
            {showPreview ? (
                <pre className="gm-preview">{preview || t('assistant:guideManager.previewEmpty')}</pre>
            ) : null}

            <table className="tm-table">
                <tbody>
                    {guides.map(g => (
                        <tr key={g.id} className={droppedIds.includes(g.id) ? 'gm-rowDropped' : ''}>
                            {editing === g.id ? (
                                <td colSpan={4}>
                                    <GuideFields
                                        value={g} ruleMax={ruleMax} busy={busy} t={t}
                                        onSave={(body) => save({ ...body, id: g.id })}
                                        onCancel={() => setEditing(null)}
                                    />
                                </td>
                            ) : (
                                <>
                                    <td className="gm-seq">{g.seq}</td>
                                    <td>
                                        <div className="gm-title">
                                            {g.title}
                                            {g.dbms ? <span className="gm-dbms">{g.dbms}</span> : null}
                                            {!g.active ? <span className="gm-off">{t('assistant:guideManager.off')}</span> : null}
                                        </div>
                                        <div className="gm-rule">{g.rule}</div>
                                    </td>
                                    <td className="gm-actions">
                                        <button type="button" className="tcf-btn" disabled={busy} onClick={() => setEditing(g.id)}>
                                            {t('common:ui.edit')}
                                        </button>
                                        {/* 끄기는 지우기와 다르다 — 규칙은 남기고 이번엔 안 쓰는 것 */}
                                        <button
                                            type="button" className="tcf-btn" disabled={busy}
                                            onClick={() => save({ ...g, active: !g.active })}
                                        >
                                            {g.active ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                                            {t(g.active ? 'assistant:guideManager.disable' : 'assistant:guideManager.enable')}
                                        </button>
                                        <button type="button" className="tcf-iconBtn" disabled={busy} onClick={() => remove(g)}>
                                            <FiTrash2 size={11} />
                                        </button>
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!guides.length ? <div className="tcf-hint">{t('assistant:guideManager.empty')}</div> : null}

            <div className="gm-add">
                <div className="tcf-label">{t('assistant:guideManager.add')}</div>
                <GuideFields
                    value={draft} ruleMax={ruleMax} busy={busy} t={t}
                    onChange={setDraft}
                    onSave={(body) => save(body)}
                />
            </div>
        </div>
    );
}

/** 규칙 입력. 남은 글자를 **세면서** 보여 준다 — 넘기고 나서 거부당하면 다시 써야 한다. */
function GuideFields({ value, ruleMax, busy, t, onChange, onSave, onCancel }) {
    const [local, setLocal] = useState(value);
    useEffect(() => { setLocal(value); }, [value]);
    const set = (k) => (e) => {
        const next = { ...local, [k]: e.target.value };
        setLocal(next);
        if (onChange) onChange(next);
    };
    const left = ruleMax - (local.rule || '').length;
    return (
        <div className="gm-fields">
            <div className="gm-row">
                <input
                    className="tcf-input" value={local.title} onChange={set('title')}
                    placeholder={t('assistant:guideManager.titlePlaceholder')}
                />
                <select className="tcf-input gm-narrow" value={local.dbms || ''} onChange={set('dbms')}>
                    {DBMS.map(d => (
                        <option key={d || 'all'} value={d}>{d || t('assistant:guideManager.allDbms')}</option>
                    ))}
                </select>
                <input
                    className="tcf-input gm-narrow" type="number" value={local.seq} onChange={set('seq')}
                    title={t('assistant:guideManager.seqHint')}
                />
            </div>
            <textarea
                className="tcf-input" rows={3} value={local.rule} onChange={set('rule')}
                placeholder={t('assistant:guideManager.rulePlaceholder')}
            />
            <div className="gm-row">
                <span className={`tcf-hint${left < 0 ? ' gm-over' : ''}`}>
                    {t('assistant:guideManager.left', { left })}
                </span>
                <button
                    type="button" className="tcf-btn tcf-primary" disabled={busy || left < 0}
                    onClick={() => onSave(local)}
                >
                    {busy ? <FiLoader size={11} className="tcf-spin" /> : (value.id ? <FiSave size={11} /> : <FiPlus size={11} />)}
                    {t(value.id ? 'common:action.save' : 'assistant:guideManager.addBtn')}
                </button>
                {onCancel ? (
                    <button type="button" className="tcf-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
                ) : null}
            </div>
            <div className="tcf-hint">{t('assistant:guideManager.ruleHint')}</div>
        </div>
    );
}

export default GuideManager;
